import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  Checkbox,
  ListItemText,
  Alert,
  Snackbar,
  MenuItem,
  Select,
  InputAdornment,
  styled,
  Avatar,
  ThemeProvider,
  CssBaseline,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { Add, Delete, Edit, Save, Visibility, Share, Cancel } from "@mui/icons-material";
import { DELETE_TENANT_URL, CREATE_TENANT_URL, UPDATE_TENANT_URL, GET_ALL_COMPANY_USERS_URL, GET_TENANT_USING_USERID_COMPANYID_URL } from "envConfig";
import { Logger } from "utils/Logger";
import { useNavigate } from "react-router-dom";
import { RootState } from "state/store";
import { useSelector } from "react-redux";
import { Tenant as Tenant_Structure} from "../../Interfaces/AutomateTest";
import { User } from "../../Interfaces/AutomateTest";
import { SharedTo } from "../../Interfaces/AutomateTest/SharedTo";

export const Tenant: React.FC = () => {
  const theme = useTheme();
  const [tenants, setTenants] = useState<Tenant_Structure[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [editRowId, setEditRowId] = useState<string | null>(null);
  const [tenantEditedRow, setTenantEditedRow] = useState<Partial<Tenant_Structure>>({});
  const [newTenantRow, setNewTenantRow] = useState<Tenant_Structure | null>(null);
  const [nameError, setNameError] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<SharedTo[]>([]);
  const [sharedTenantId, setShareTenantId] = useState<string | null>(null); // Changed type to string | null
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  
  const user = useSelector((state: RootState) => state.authentication.user);
  
  // Fetch tenants from the API
    const fetchTenants = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${GET_TENANT_USING_USERID_COMPANYID_URL}/?userId=${user._id}&companyId=${user.companyId}`, {
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
            setLoading(false);
            Logger.error("st-ck-client/src/modules/AutomateTest/Tenant/Tenant.ts : fetchTenants(): issue while getting Tenant, response is " , response.statusText);
          throw new Error(`Error: ${response.status}`);
        }

        const data = await response.json();
        if (Array.isArray(data.data)) {
          setTenants(data.data);
          setLoading(false);
        } else {
            Logger.error("st-ck-client/src/modules/AutomateTest/Tenant/Tenant.ts : fetchTenants(): ", "Unexpected API response format: " + JSON.stringify(data.data, null, 2));
            setTenants([]);
            setLoading(false);
        }
      } catch (error) {
        Logger.error("st-ck-client/src/modules/AutomateTest/Tenant/Tenant.ts : fetchTenants(): ", "Failed to fetch tenants: "+ error);
        setTenants([]);
        setLoading(false);
      }
    };
    useEffect(() => {
        fetchTenants();
    }, []);

    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await fetch(GET_ALL_COMPANY_USERS_URL+`/${user.companyId}`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
            setLoading(false);
            Logger.error("st-ck-client/src/modules/AutomateTest/Tenant/Tenant.ts : fetchUsers(): issue while getting Users, response is " , response.statusText);
            throw new Error(`Error: ${response.status}`);
        }

        const data = await response.json();
        if (Array.isArray(data.data)) {
            setUsers(data.data);
            setLoading(false);
            Logger.debug("st-ck-client/src/modules/AutomateTest/Tenant/Tenant.ts : fetchUsers(): ", "fetched users are: " + JSON.stringify(data.data, null, 2));

        } else {
            Logger.error("st-ck-client/src/modules/AutomateTest/Tenant/Tenant.ts : fetchUsers(): ", "Unexpected API response format: " + JSON.stringify(data.data, null, 2));
            setUsers([]);
            setLoading(false);
        }
      } catch (error) {
        Logger.error("st-ck-client/src/modules/AutomateTest/Tenant/Tenant.ts : fetchUsers(): ", "Failed to fetch users: "+ error);
        setUsers([]);
        setLoading(false);
      }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

  // Add a new row
  const handleAddNewTenantRow = () => {
    const newTenant: Tenant_Structure = {
      _id: "",
      name: "",
      description: "",
      accessLevel: "private",
      userId: user._id,
      url: "",
      companyId: user.companyId,
      testSuiteIds: [],
      updatedAt: new Date().toISOString(),
    };
    setNewTenantRow(newTenant);
  };

  const handleTenantInputChange = (_id: string, field: keyof Tenant_Structure, value: unknown) => {
    if (_id === "" && newTenantRow) {
      setNewTenantRow((prev) => prev ? { ...prev, [field]: value } : null);
      if (field === "name" && value) {
        setNameError(false);
      }
    } else if (editRowId === _id) {
        setTenantEditedRow((prev) => ({ ...prev, [field]: value }));
    } else {
        setTenants((prev) =>
        prev.map((tenant) =>
            tenant._id === _id ? { ...tenant, [field]: value } : tenant
        )
      );
    }
  };
  // Save new row to API
  const handleCreateNewTenant = async () => {
    if (newTenantRow) {
      try {
        if (!newTenantRow.name) {
            setNameError(true);
            return;
          }
        setLoading(true);
        const response = await fetch(CREATE_TENANT_URL, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(newTenantRow),
        });

        if (!response.ok) {
          Logger.error("st-ck-client/src/modules/AutomateTest/Tenant/Tenant.ts : handleSaveNewRow(): issue while creating Tenant, response is " , + JSON.stringify(newTenantRow));
          setLoading(false);
          throw new Error(`Error: ${response.status}`);
        }
        await fetchTenants();
        setNewTenantRow(null);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        Logger.error("st-ck-client/src/modules/AutomateTest/Tenant/Tenant.ts : handleSaveNewRow(): ", "Failed to save new tenant: "+ error);
      }
    }
  };

 
  const handleTenantEdit = (_id: string) => {
    setEditRowId(_id);
    const tenant = tenants.find((tenant) => tenant._id === _id);
    if (tenant) setTenantEditedRow(tenant);
  };

  const handleUpdateTenant = async (_id:string) => {
    try {
        setLoading(true);
        const tenantToUpdate = tenants.find((tenant) => tenant._id === _id);
        if (!tenantToUpdate) {
          setLoading(false);
            Logger.error("st-ck-client/src/modules/AutomateTest/Tenant/Tenant.ts : handleUpdateTenant(): Unable to find the tenant with id: " , _id);
            return;
        }
        // If updating an existing tenant, selectedUsers should reflect its current sharedTo, plus any changes
        // If tenantEditedRow exists, it means we're updating general tenant properties.
        // If not, it means we're likely just updating sharedTo from the share dialog.
        const payload = tenantEditedRow && Object.keys(tenantEditedRow).length > 0
            ? { ...tenantToUpdate, ...tenantEditedRow }
            : { ...tenantToUpdate, sharedTo: selectedUsers }; // Apply selectedUsers if no other edits

        Logger.info("st-ck-client/src/modules/AutomateTest/Tenant/Tenant.ts : handleUpdateTenant(): update tenant Payload " , JSON.stringify(payload, null, 2));
        const response = await fetch(`${UPDATE_TENANT_URL}/${_id}`, {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
      });

      if (!response.ok) {
        Logger.error("st-ck-client/src/modules/AutomateTest/Tenant/Tenant.ts : handleUpdateTenant(): issue while updating Tenant, response is " , response.statusText);
        setLoading(false);
        throw new Error(`Error: ${response.statusText}`);
      }

      setTenants((prev) =>
        prev.map((tenant) =>
            tenant._id === _id ? { ...tenant, ...tenantEditedRow, ...(payload.sharedTo ? { sharedTo: payload.sharedTo } : {}) } : tenant
        )
      );
      setEditRowId(null);
      setTenantEditedRow({});
      setSelectedUsers([]); // Clear selected users after successful update
      setOpen(false);
      setLoading(false);
    } catch (error) {
        setLoading(false);
        Logger.error("st-ck-client/src/modules/AutomateTest/Tenant/Tenant.ts : handleUpdateTenant(): ", "Failed to save tenant:" + error);
    }
  };
 
  const handleCancelEdit = () => {
    setNewTenantRow(null);
    setEditRowId(null);
    setTenantEditedRow({});
  };

  const handleDeleteTenant = async(_id: string) => {
    try {
        setLoading(true);
        const response = await fetch(DELETE_TENANT_URL+`/${_id}`, {
          method: "DELETE",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });
  
        if (!response.ok) {
            Logger.error("st-ck-client/src/modules/AutomateTest/Tenant/Tenant.ts : handleDeleteTenant(): issue while deleting Tenant, response is " , response.statusText);
            setLoading(false);
            throw new Error(`Error: ${response.status}`);
        }
        await fetchTenants();
        setLoading(false);
      } catch (error) {
        setLoading(false);
        Logger.error("st-ck-client/src/modules/AutomateTest/Tenant/Tenant.ts : handleDeleteTenant(): ", "Failed to delete tenants:"+ error);
      }
  };

  
const handleToggle = (user: User) => {
      const sharedWith: SharedTo = {
        userId: user._id,
        permission: "readonly" // Default permission
    }

    setSelectedUsers((prev) =>
      prev.some((u) => u.userId === user._id)
        ? prev.filter((u) => u.userId !== user._id)
        : [...prev, sharedWith]
    );
  };
  
    const handleShare = (_id:string) => {
        setShareTenantId(_id);
        const tenantToShare = tenants.find(t => t._id === _id);
        if (tenantToShare && tenantToShare.sharedTo) {
            // Pre-populate selectedUsers with existing sharedTo data
            setSelectedUsers(tenantToShare.sharedTo);
        } else {
            setSelectedUsers([]); // Reset if no existing shared users
        }
        setOpen(true);
    };

    // Corrected function for handling permission change within the share dialog
    const handleSharedUserPermissionChange = (userId: string, permission: string) => {
        setSelectedUsers((prev) =>
            prev.map((u) => (u.userId === userId ? { ...u, permission: permission as 'readonly' | 'readwrite' } : u))
        );
    };
      
      const UserAvatar = styled(Avatar)(() => ({ // Removed unused `theme` prop
        width: 48,
        height: 48,
        transition: "transform 0.2s",
        "&:hover": {
          transform: "scale(1.1)",
          cursor: "pointer"
        }
      }));

  return ( loading ?
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Backdrop
        sx={(theme) => ({ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 })}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </ThemeProvider>
    :
    <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
      <Typography variant="h4" gutterBottom>
        All Tenants
      </Typography>
        <Button variant="contained" sx={{ backgroundColor: theme.palette.primary.main }} startIcon={<Add />} onClick={handleAddNewTenantRow}>
          New Tenant
        </Button>
      </Box>
     <TableContainer component={Paper} sx={{ maxHeight: "550px", overflow: "auto" }}>
             <Table sx={{ tableLayout: "auto", "& td, & th": { maxWidth: "250px", width:"180px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}}>
               <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Modified On</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tenants.map((tenant, index) => (
              <TableRow key={tenant._id}>
                <TableCell>{index}</TableCell>
                <TableCell>
                  {editRowId === tenant._id ? (
                    <TextField size="small"
                      value={tenantEditedRow.name || ""}
                      onChange={(e) => handleTenantInputChange(tenant._id, "name", e.target.value)} error={nameError}
                      helperText={nameError ? "Name is required" : ""}
                      label="Name"
                    />
                  ) : (
                    tenant.name
                  )}
                </TableCell>
                <TableCell>
                  {editRowId === tenant._id ? (
                    <TextField size="small"
                      value={tenantEditedRow.description || ""}
                      onChange={(e) => handleTenantInputChange(tenant._id, "description", e.target.value)} label="Description"
                    />
                  ) : (
                    tenant.description
                  )}
                </TableCell>
                <TableCell>{tenant.accessLevel}</TableCell>
                <TableCell>{tenant.updatedAt}</TableCell>
                <TableCell>
                  {editRowId === tenant._id ? (
                    <>
                    <IconButton onClick={() => handleUpdateTenant(tenant._id)}>
                      <Save />
                    </IconButton>
                    <IconButton onClick={handleCancelEdit}>
                      <Cancel />
                    </IconButton>
                    </>
                  ) : (
                    <>
                    <IconButton onClick={() => handleTenantEdit(tenant._id)}>
                      <Edit />
                    </IconButton>
                  <IconButton onClick={() => navigate(`/tenant/${tenant._id}`)}>
                    <Visibility />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteTenant(tenant._id)}>
                    <Delete />
                  </IconButton>
                  <IconButton  onClick={() => handleShare(tenant._id)}>
                    <Share />
                  </IconButton>
                  </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {newTenantRow && (
              <TableRow>
                <TableCell>New</TableCell>
                <TableCell>
                  <TextField size="small"
                    value={newTenantRow.name}
                    onChange={(e) => handleTenantInputChange("", "name", e.target.value)} error={nameError}
                    helperText={nameError ? "Name is required" : ""}
                    label="Name"
                  />
                </TableCell>
                <TableCell>
                  <TextField size="small"
                    value={newTenantRow.description}
                    onChange={(e) => handleTenantInputChange("", "description", e.target.value)} label="Description"
                  />
                </TableCell>
                <TableCell>{newTenantRow.accessLevel}</TableCell>
                <TableCell>{newTenantRow.updatedAt}</TableCell>
                <TableCell>
                  <IconButton onClick={handleCreateNewTenant}>
                    <Save />
                  </IconButton>
                  <IconButton onClick={handleCancelEdit}>
                    <Cancel />
                  </IconButton>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{"Share Tenant"}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" mb={2}>
            Select users to share this tenant with:
          </Typography>
          <List>
            {users.map((user) => (
              <ListItem
                key={user._id}
                onClick={() => handleToggle(user)}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={selectedUsers.some((u) => u.userId === user._id)}
                    tabIndex={-1}
                    disableRipple
                  />
                {/* Ensure avatar path is correct based on your User interface */}
                <UserAvatar
                  src={user.profile?.avatar || `https://www.gravatar.com/avatar/${user._id}?d=retro`} // Fallback avatar
                  alt={user.profile.firstName || "User"}
                  aria-label={`${user.profile.firstName || "User"}'s avatar`}
                />
                </ListItemIcon>
                <ListItemText primary={user.profile.firstName} secondary={user.email} />
                <Select
                  value={selectedUsers.find(u => u.userId === user._id)?.permission || 'readonly'} // Set value from selectedUsers
                  onChange={(e) => handleSharedUserPermissionChange(user._id, e.target.value)} // Use the new handler
                  size="small"
                  sx={{ minWidth: 120 }}
                >
                  <MenuItem value="readwrite">Editor</MenuItem> {/* Changed from Owner */}
                  <MenuItem value="readonly">Viewer</MenuItem>
                </Select>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
        <Button onClick={() => setOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={() => handleUpdateTenant(sharedTenantId!)} // Use non-null assertion as sharedTenantId is set
            color="primary"
            variant="contained"
            disabled={selectedUsers.length === 0}
          >
            Share
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
